import React from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { User, Lock, Settings2, Bell, ShieldCheck, AlertTriangle, ChevronLeft } from 'lucide-react';

const SECTIONS = [
  { key: 'info', label: 'Informations', icon: User },
  { key: 'security', label: 'Sécurité', icon: Lock },
  { key: 'preferences', label: 'Préférences', icon: Settings2 },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'access', label: 'Accès & Permissions', icon: ShieldCheck },
  { key: 'danger', label: 'Zone sensible', icon: AlertTriangle, danger: true },
] as const;

export type ProfileSectionKey = typeof SECTIONS[number]['key'];

interface ProfileSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeSection: ProfileSectionKey;
  onSectionChange: (key: ProfileSectionKey) => void;
}

export function ProfileSidebar({ collapsed, onToggle, activeSection, onSectionChange }: ProfileSidebarProps) {
  if (collapsed) {
    return (
      <div className="w-[52px] bg-background rounded-xl border flex flex-col items-center py-3 gap-1">
        <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-muted mb-2">
          <ChevronLeft className="h-4 w-4 text-muted-foreground rotate-180" />
        </button>
        {SECTIONS.map((section) => {
          if (!section?.icon) return null;
          const Icon = section.icon;
          const isActive = activeSection === section.key;
          const isDanger = 'danger' in section && section.danger;
          return (
            <button
              key={section.key}
              onClick={() => onSectionChange(section.key)}
              className={cn(
                'p-2 rounded-lg transition-colors',
                isActive
                  ? isDanger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                  : isDanger ? 'text-destructive/50 hover:bg-destructive/5' : 'text-muted-foreground hover:bg-muted',
              )}
              title={section.label}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-[240px] bg-background rounded-xl border flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Profil</span>
        </div>
        <button onClick={onToggle} className="p-1 rounded-lg hover:bg-muted">
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-0.5">
        {SECTIONS.map((section) => {
          if (!section?.icon) return null;
          const Icon = section.icon;
          const isActive = activeSection === section.key;
          const isDanger = 'danger' in section && section.danger;
          return (
            <button
              key={section.key}
              onClick={() => onSectionChange(section.key)}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all',
                isActive
                  ? isDanger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                  : isDanger ? 'text-destructive/60 hover:bg-destructive/5 hover:text-destructive' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span>{section.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
